import {Component, Inject, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {ReportService} from '../../../services/modules/report.service';
import {EmployerService} from '../../../services/modules/employer.service';
import {CompanyModel} from '../../../models/CompanyModel';
import { saveAs } from 'file-saver';

export type DialogAnuncioInformativoMode = 'PRENOMINA' | 'PAYROLL_CONFIG' | 'REPROGRAMACION' | 'ENTRY_EXIT_DETAIL';

export interface DialogAnuncioInformativoData {
  item?: any;
  company: CompanyModel;
  mode?: DialogAnuncioInformativoMode;
  title?: string;
  maxExtraHoursPerPeriod?: number;
  selectedDays?: number[];
  employerInfoId?: string;
  readOnly?: boolean;
  personalRow?: any;
  // PAYROLL_CONFIG — creación/cierre automático de períodos
  periodType?: number | null;
  creationStartDate?: string | Date | null;
  isCreationEnabled?: boolean;
  closingBusinessDays?: number | null;
  isClosingEnabled?: boolean;
  periodState?: 'OPEN' | 'CLOSED' | null;
}

@Component({
  selector: 'app-dialog-anuncio-informativo',
  templateUrl: './dialog-anuncio-informativo.component.html',
  styleUrls: ['./dialog-anuncio-informativo.component.scss']
})
export class DialogAnuncioInformativoComponent implements OnInit {
  item: any;
  company: CompanyModel;
  selectedFormat = 'horizontal';

  // PAYROLL_CONFIG state
  maxExtraHours: number | null = null;
  selectedDays: number[] = [];
  readonly weekdays = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];

  // PAYROLL_CONFIG — creación/cierre automático de períodos (PayrollTimeAutomation)
  periodType: number | null = null;
  creationStartDate: Date | null = null;
  isCreationEnabled = false;
  closingBusinessDays: number | null = null;
  isClosingEnabled = false;
  periodState: 'OPEN' | 'CLOSED' | null = null;

  readonly periodTypes = [
    { value: 1, label: 'Catorcenal' },
    { value: 2, label: 'Quincenal' },
    { value: 3, label: 'Mensual' }
  ];

  get canEnableCreation(): boolean {
    return !!this.creationStartDate;
  }

  get canEnableClosing(): boolean {
    return !!this.closingBusinessDays && this.closingBusinessDays >= 1;
  }

  // REPROGRAMACION state
  reprEntry: string = '';
  reprLeave: string = '';
  reprBreak: string = '';
  reprComment: string = '';
  reprLoading: boolean = false;
  reprSaving: boolean = false;
  private _reprEntryOriginal: string = '';
  private _reprLeaveOriginal: string = '';
  private _reprBreakOriginal: string = '';
  private _leaveAutoRecalculated: boolean = false;
  private _reprPersonalEmployerId: number | null = null;
  private _reprDateStr: string = '';
  // Totales desde API
  private _scheduledWorkdaySpan: string = '';
  private _scheduledWorkedHours: string = '';
  private _rescheduledWorkdaySpan: string = '';
  private _rescheduledLeaveOnNextDay: boolean = false;

  get reprIsLocked(): boolean {
    const status = this.data.personalRow?.ReviewStatusValue;
    return this.data.readOnly || status === 'Approved' || status === 'Rejected';
  }

  get reprStatusColor(): string {
    const s = (this.data.personalRow?.ReviewStatusValue || '').toLowerCase();
    if (s === 'approved') { return '#16a34a'; }
    if (s === 'rejected') { return '#dc2626'; }
    if (s === 'reviewed') { return '#2563eb'; }
    return '#fe8c00';
  }

  get reprStatusBg(): string {
    const s = (this.data.personalRow?.ReviewStatusValue || '').toLowerCase();
    if (s === 'approved') { return '#dcfce7'; }
    if (s === 'rejected') { return '#fee2e2'; }
    if (s === 'reviewed') { return '#dbeafe'; }
    return '#fff3e0';
  }

  get reprPersonName(): string {
    const row = this.data.personalRow;
    if (!row) { return ''; }
    return row.PersonalName || `${row.Name || ''} ${row.LastName || ''}`.trim();
  }

  get reprFormattedDate(): string {
    const d = this.data.personalRow?.Date;
    if (!d) { return ''; }
    const date = new Date(d);
    if (isNaN(date.getTime())) { return String(d); }
    const days = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
    const months = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
                    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
    return `${days[date.getDay()]} ${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
  }

  get reprShortDate(): string {
    const d = this.data.personalRow?.Date;
    if (!d) { return ''; }
    const date = new Date(d);
    if (isNaN(date.getTime())) { return ''; }
    const dd = String(date.getDate()).padStart(2, '0');
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const yy = String(date.getFullYear()).slice(-2);
    return `${dd}/${mm}/${yy}`;
  }

  get reprStatusLabel(): string {
    const s = this.data.personalRow?.ReviewStatusValue || '';
    if (s === 'Approved') { return 'APROBADO'; }
    if (s === 'Rejected') { return 'RECHAZADO'; }
    if (s === 'Reviewed') { return 'REVISADO'; }
    if (s === 'Reset') { return 'REINICIADO'; }
    return 'SIN REVISAR';
  }

  get reprEntryStatus(): 'fijo' | 'mod' {
    return this.reprEntry !== this._reprEntryOriginal ? 'mod' : 'fijo';
  }

  get reprLeaveStatus(): 'fijo' | 'mod' | 'recalc' {
    if (this.reprLeave === this._reprLeaveOriginal) { return 'fijo'; }
    return this._leaveAutoRecalculated ? 'recalc' : 'mod';
  }

  get reprBreakStatus(): 'fijo' | 'mod' {
    return this.reprBreak !== this._reprBreakOriginal ? 'mod' : 'fijo';
  }

  get reprBreakScheduled(): string {
    return this._reprBreakOriginal;
  }

  get reprScheduledEntry(): string {
    return this._reprEntryOriginal;
  }

  get reprScheduledLeave(): string {
    return this._reprLeaveOriginal;
  }

  get reprJornadaScheduled(): string {
    if (this._scheduledWorkdaySpan) { return this.toTimeInput(this._scheduledWorkdaySpan); }
    const e = this.timeToMinutes(this._reprEntryOriginal);
    const l = this.timeToMinutes(this._reprLeaveOriginal);
    if (e === null || l === null) { return '—'; }
    let d = l - e; if (d < 0) { d += 1440; }
    return this.minutesToHHmm(d);
  }

  get reprJornadaExcepcion(): string {
    if (this._rescheduledWorkdaySpan) { return this.toTimeInput(this._rescheduledWorkdaySpan); }
    const e = this.timeToMinutes(this.reprEntry);
    const l = this.timeToMinutes(this.reprLeave);
    if (e === null || l === null) { return '—'; }
    let d = l - e;
    if (d < 0 || this._rescheduledLeaveOnNextDay) { d += 1440; }
    return this.minutesToHHmm(d);
  }

  get reprJornadaStatus(): 'fijo' | 'recalc' {
    return this.reprJornadaExcepcion !== this.reprJornadaScheduled ? 'recalc' : 'fijo';
  }

  get reprTrabajadasScheduled(): string {
    if (this._scheduledWorkedHours) { return this.toTimeInput(this._scheduledWorkedHours); }
    const j = this.timeToMinutes(this.reprJornadaScheduled);
    const b = this.timeToMinutes(this._reprBreakOriginal);
    if (j === null) { return '—'; }
    return this.minutesToHHmm(j - (b ?? 0));
  }

  get reprTrabajadasExcepcion(): string {
    // Las horas trabajadas son siempre las mismas (el servidor recalcula la salida, no las trabajadas)
    return this.reprTrabajadasScheduled;
  }

  get mode(): DialogAnuncioInformativoMode {
    return this.data.mode ?? 'PRENOMINA';
  }

  get dialogTitle(): string {
    if (this.data.title) { return this.data.title; }
    if (this.mode === 'PAYROLL_CONFIG') { return 'CONFIGURACIÓN DE PERIODOS'; }
    if (this.mode === 'REPROGRAMACION') { return 'REPROGRAMACIÓN'; }
    if (this.mode === 'ENTRY_EXIT_DETAIL') { return 'DETALLE DE MARCACIONES'; }
    return 'INFORME DE PRENOMINA';
  }

  constructor(
    public dialogRef: MatDialogRef<DialogAnuncioInformativoComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogAnuncioInformativoData,
    private reportService: ReportService,
    private employerService: EmployerService
  ) {
    this.item = data.item;
    this.company = data.company;
  }

  ngOnInit(): void {
    if (this.mode === 'PAYROLL_CONFIG') {
      this.maxExtraHours = this.data.maxExtraHoursPerPeriod ?? 0;
      this.selectedDays  = this.data.selectedDays ? [...this.data.selectedDays] : [];
      this.periodType = this.data.periodType ?? null;
      this.creationStartDate = this.data.creationStartDate ? new Date(this.data.creationStartDate) : null;
      this.isCreationEnabled = this.data.isCreationEnabled ?? false;
      this.closingBusinessDays = this.data.closingBusinessDays ?? 0;
      this.isClosingEnabled = this.data.isClosingEnabled ?? false;
      this.periodState = this.data.periodState ?? null;
    }
    if (this.mode === 'REPROGRAMACION') {
      const row = this.data.personalRow ?? {};
      this._reprPersonalEmployerId = row.PersonalEmployerId ?? row.Id ?? null;
      const rawDate: string = row.Date ?? '';
      this._reprDateStr = rawDate ? rawDate.substring(0, 10) : '';

      // Fallback local mientras carga la API
      this._reprEntryOriginal = this.toTimeInput(row.ScheduledEntry || row.ScheduledEntryTime || '');
      this._reprLeaveOriginal = this.toTimeInput(row.ScheduledLeave || row.ScheduledLeaveTime || '');
      this._reprBreakOriginal = this.toTimeInput(row.BreakTime || row.ScheduledBreak || row.ScheduledBreakTime || '');
      this.reprEntry = this.toTimeInput(row.CalculatedEntryTime || row.ScheduledEntry || row.ScheduledEntryTime || '');
      this.reprLeave = this.toTimeInput(row.CalculatedLeaveTime || row.ScheduledLeave || row.ScheduledLeaveTime || '');
      this.reprBreak = this.toTimeInput(row.CalculatedBreakTime || row.BreakTime || row.ScheduledBreak || row.ScheduledBreakTime || '');
      this.reprComment = '';
      this._leaveAutoRecalculated = false;

      if (this._reprPersonalEmployerId && this._reprDateStr) {
        this.reprLoading = true;
        this.employerService.getShiftReschedule(this._reprPersonalEmployerId, this._reprDateStr)
          .subscribe({
            next: (res: any) => {
              // Programado (fuente de verdad del servidor)
              this._reprEntryOriginal = this.toTimeInput(res.ScheduledEntryTime || '');
              this._reprLeaveOriginal = this.toTimeInput(res.ScheduledLeaveTime || '');
              this._reprBreakOriginal = this.toTimeInput(res.ScheduledBreakTime || '');
              this._scheduledWorkdaySpan  = res.ScheduledWorkdaySpan  || '';
              this._scheduledWorkedHours  = res.ScheduledWorkedHours  || '';

              if (res.HasOverride) {
                // Reprogramación existente — cargar campos Rescheduled*
                this.reprEntry  = this.toTimeInput(res.RescheduledEntryTime  || res.ScheduledEntryTime  || '');
                this.reprLeave  = this.toTimeInput(res.RescheduledLeaveTime  || res.ScheduledLeaveTime  || '');
                this.reprBreak  = this.toTimeInput(res.RescheduledBreakTime  || res.ScheduledBreakTime  || '');
                this._rescheduledWorkdaySpan    = res.RescheduledWorkdaySpan   || '';
                this._rescheduledLeaveOnNextDay = res.RescheduledLeaveOnNextDay ?? false;
                this.reprComment = res.Reason || '';
              } else {
                // Sin override — inicializar con valores programados
                this.reprEntry  = this.toTimeInput(res.ScheduledEntryTime  || '');
                this.reprLeave  = this.toTimeInput(res.ScheduledLeaveTime  || '');
                this.reprBreak  = this.toTimeInput(res.ScheduledBreakTime  || '');
                this._rescheduledWorkdaySpan    = '';
                this._rescheduledLeaveOnNextDay = false;
              }
              this._leaveAutoRecalculated = false;
              this.reprLoading = false;
            },
            error: () => { this.reprLoading = false; }
          });
      }
    }
  }

  toggleDay(index: number): void {
    const pos = this.selectedDays.indexOf(index);
    if (pos === -1) {
      this.selectedDays = [...this.selectedDays, index].sort((a, b) => a - b);
    } else {
      this.selectedDays = this.selectedDays.filter(d => d !== index);
    }
  }

  isDaySelected(index: number): boolean {
    return this.selectedDays.includes(index);
  }

  onCreationStartDateChange(): void {
    if (!this.canEnableCreation) {
      this.isCreationEnabled = false;
    }
  }

  onClosingBusinessDaysChange(): void {
    if ((this.closingBusinessDays ?? 0) < 0) {
      this.closingBusinessDays = 0;
    }
    if (!this.canEnableClosing) {
      this.isClosingEnabled = false;
    }
  }

  incClosingBusinessDays(): void {
    this.closingBusinessDays = (this.closingBusinessDays ?? 0) + 1;
    this.onClosingBusinessDaysChange();
  }

  decClosingBusinessDays(): void {
    this.closingBusinessDays = Math.max(0, (this.closingBusinessDays ?? 0) - 1);
    this.onClosingBusinessDaysChange();
  }

  onMaxExtraHoursChange(): void {
    if ((this.maxExtraHours ?? 0) < 0) {
      this.maxExtraHours = 0;
    }
  }

  incMaxExtraHours(): void {
    this.maxExtraHours = (this.maxExtraHours ?? 0) + 1;
  }

  decMaxExtraHours(): void {
    this.maxExtraHours = Math.max(0, (this.maxExtraHours ?? 0) - 1);
  }

  blockNegativeKey(event: KeyboardEvent): void {
    if (event.key === '-' || event.key === '+' || event.key === 'e' || event.key === 'E') {
      event.preventDefault();
    }
  }

  private downloadPrenominaHorizontal(): void {
    const item = this.item;

    if (item && item.StatusName === 'Finished') {
      const filter =
        `extratime?companyId=${this.company.CompanyInfoParentId}` +
        `&projectId=${item.PlaceOfWorkId}` +
        `&employerId=${item.EmployerInfoId}` +
        `&payrollTimeId=${item.PayrollTimeId}`;

      this.reportService.downloadReport(filter).subscribe(res => {
        saveAs(res, 'Prenomina');
        this.dialogRef.close(true);
      });
    } else {
      this.dialogRef.close(false);
    }
  }

  private downloadPrenominaVertical(): void {
    const item = this.item;

    if (item && item.StatusName === 'Finished') {
      const filter =
        `extratimerows?companyId=${this.company.CompanyInfoParentId}` +
        `&adminInfoId=${this.company.CompanyInfoParentId}` +
        `&projectId=${item.PlaceOfWorkId}` +
        `&employerId=${item.EmployerInfoId}` +
        `&payrollTimeId=${item.PayrollTimeId}`;

      this.reportService.downloadReport(filter).subscribe(res => {
        saveAs(res, 'Prenomina');
        this.dialogRef.close(true);
      });
    } else {
      this.dialogRef.close(false);
    }
  }

  private downloadPrenominaVerticalProjects(): void {
    const item = this.item;

    if (item && item.StatusName === 'Finished') {
      const filter =
        `extratimeprojects?companyId=${this.company.CompanyInfoParentId}` +
        `&adminInfoId=${this.company.CompanyInfoParentId}` +
        `&projectId=${item.PlaceOfWorkId}` +
        `&employerId=${item.EmployerInfoId}` +
        `&payrollTimeId=${item.PayrollTimeId}`;

      this.reportService.downloadReport(filter).subscribe(res => {
        saveAs(res, 'Prenomina');
        this.dialogRef.close(true);
      });
    } else {
      this.dialogRef.close(false);
    }
  }

  private downloadPrenominaVerticalProjectsComp(): void {
    const item = this.item;

    if (item && item.StatusName === 'Finished') {
      const filter =
        `extratimerows-redistributed?companyId=${this.company.CompanyInfoParentId}` +
        `&adminInfoId=${this.company.CompanyInfoParentId}` +
        `&projectId=${item.PlaceOfWorkId}` +
        `&employerId=${item.EmployerInfoId}` +
        `&payrollTimeId=${item.PayrollTimeId}`;

      this.reportService.downloadReport(filter).subscribe(res => {
        saveAs(res, 'Prenomina');
        this.dialogRef.close(true);
      });
    } else {
      this.dialogRef.close(false);
    }
  }

  private downloadPrenominaVerticalByCompProject(): void {
    const item = this.item;

    if (item && item.StatusName === 'Finished') {
      const filter =
        `extratimeprojects-redistributed?companyId=${this.company.CompanyInfoParentId}` +
        `&adminInfoId=${this.company.CompanyInfoParentId}` +
        `&projectId=${item.PlaceOfWorkId}` +
        `&employerId=${item.EmployerInfoId}` +
        `&payrollTimeId=${item.PayrollTimeId}`;

      this.reportService.downloadReport(filter).subscribe(res => {
        saveAs(res, 'Prenomina');
        this.dialogRef.close(true);
      });
    }
  }

  save(): void {
    if (this.mode === 'REPROGRAMACION') {
      if (!this._reprPersonalEmployerId || !this._reprDateStr) {
        this.dialogRef.close({ confirmed: false });
        return;
      }
      const body: any = {
        personalEmployerId: this._reprPersonalEmployerId,
        date: this._reprDateStr,
        rescheduledEntryTime: this.reprEntry ? this.reprEntry + ':00' : null,
      };
      if (this.reprBreak && this.reprBreak.trim()) {
        const b = this.reprBreak.trim();
        body.rescheduledBreakTime = b.match(/^\d{1,2}:\d{2}$/) ? b + ':00' : b;
      }
      if (this.reprComment.trim()) { body.reason = this.reprComment.trim(); }

      this.reprSaving = true;
      this.employerService.postShiftReschedule(body).subscribe({
        next: () => {
          this.reprSaving = false;
          this.dialogRef.close({
            confirmed: true,
            calculatedEntryTime: this.reprEntry,
            calculatedLeaveTime: this.reprLeave,
            calculatedBreakTime: this.reprBreak,
            comment: this.reprComment.trim(),
          });
          // Llamar CalculateSync en paralelo (fire & forget)
          const row = this.data.personalRow ?? {};
          const calcEmployerInfoId: string = row.EmployerInfoId ?? '';
          const calcDate: string = row.Date ?? '';
          const calcPlaceOfWorkId: string = row.PlaceOfWorkId ?? '';
          const calcPersonalInfoId: string = row.PersonalCompanyInfoId ?? '';
          if (calcEmployerInfoId && calcDate && calcPlaceOfWorkId && calcPersonalInfoId) {
            this.employerService.postCalculateByDatePerson(
              calcEmployerInfoId, calcDate, calcPlaceOfWorkId, calcPersonalInfoId
            ).subscribe();
          }
        },
        error: () => { this.reprSaving = false; }
      });
      return;
    }
    if (this.mode === 'PAYROLL_CONFIG') {
      const result = {
        confirmed: true,
        maxExtraHoursPerPeriod: this.maxExtraHours,
        selectedDays: this.selectedDays,
        periodType: this.periodType,
        creationStartDate: this.creationStartDate,
        isCreationEnabled: this.isCreationEnabled && this.canEnableCreation,
        closingBusinessDays: this.closingBusinessDays,
        isClosingEnabled: this.isClosingEnabled && this.canEnableClosing,
        periodState: this.periodState
      };

      this.dialogRef.close(result);
      return;
    }
    switch (this.selectedFormat) {
      case 'horizontal':
        this.downloadPrenominaHorizontal();
        break;

      case 'vertical':
        this.downloadPrenominaVertical();
        break;

      case 'verticalByProjectComp':
        this.downloadPrenominaVerticalProjectsComp();
        break;

        case 'verticalByCompProject':
        this.downloadPrenominaVerticalByCompProject();
        break;

      default:
        this.downloadPrenominaVerticalProjects();
        break;
    }
  }

  close(): void {
    this.dialogRef.close(false);
  }

  toShortTime(val: string): string {
    if (!val) { return '—'; }
    const m = val.match(/(\d{1,2}):(\d{2})/);
    if (!m) { return val; }
    let h = parseInt(m[1], 10);
    const min = m[2];
    const period = h >= 12 ? 'PM' : 'AM';
    h = h % 12 || 12;
    return `${String(h).padStart(2, '0')}:${min} ${period}`;
  }

  formatTimePeriod(val: string | null | undefined): string {
    if (!val || val === 'null') { return ''; }
    const m = val.match(/(\d{1,2}):(\d{2})/);
    if (!m) { return val; }
    const h = parseInt(m[1], 10);
    const min = m[2];
    const period = h >= 12 ? 'PM' : 'AM';
    return `${String(h).padStart(2, '0')}:${min} ${period}`;
  }

  toTimeInput(val: string): string {
    if (!val) { return ''; }
    const m = val.match(/(\d{2}:\d{2})/);
    return m ? m[1] : '';
  }

  reprBadgeStatus(current: string, scheduled: string): 'fijo' | 'mod' {
    const a = this.toTimeInput(current);
    const b = this.toTimeInput(scheduled);
    return (!a || a === b) ? 'fijo' : 'mod';
  }

  onReprEntryChange(): void {
    // El usuario editó manualmente → limpiar valores de API para recalcular localmente
    this._rescheduledWorkdaySpan = '';
    this._rescheduledLeaveOnNextDay = false;
    const origEntry = this.timeToMinutes(this._reprEntryOriginal);
    const origLeave = this.timeToMinutes(this._reprLeaveOriginal);
    const newEntry  = this.timeToMinutes(this.reprEntry);
    if (origEntry !== null && origLeave !== null && newEntry !== null) {
      let dur = origLeave - origEntry;
      if (dur < 0) { dur += 1440; }
      this.reprLeave = this.minutesToHHmm((newEntry + dur) % 1440);
      this._leaveAutoRecalculated = true;
    }
  }

  onReprLeaveChange(): void {
    this._leaveAutoRecalculated = false;
  }

  private timeToMinutes(time: string): number | null {
    if (!time) { return null; }
    const m = time.match(/^(\d{1,2}):(\d{2})$/);
    if (!m) { return null; }
    return parseInt(m[1], 10) * 60 + parseInt(m[2], 10);
  }

  private minutesToHHmm(minutes: number): string {
    const h = Math.floor(minutes / 60) % 24;
    const m = minutes % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  }

}
